import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { InfoOutlineIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Icon, Tab, Table, TableContainer, Tbody, Td, Th, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { BsTrash } from "react-icons/bs";
import { MdOutlineModeEdit } from "react-icons/md";

import Loader from "@/components/Loader";
import { callApiWithToken } from "@/utils/http_common";

import Typography from "../../../components/Common/Typography";
import AcceptReqModal from "../modals/AcceptReqModal";
import DeleteInviteModal from "../modals/DeleteInviteModal";
import DeleteModal from "../modals/DeleteModal";
import DeleteReqModal from "../modals/DeleteReqModal";
import EditModal from "../modals/EditModal";
import InvitationModal from "../modals/InvitationModal";

const Teams = ({ team, joinRequests, refreshData }) => {
  const userDetails = useSelector((state) => state?.login?.userDetails);
  const canCreateInvitation = userDetails?.permissions?.includes("member_invite.create");
  const canDeleteInvitation = userDetails?.permissions?.includes("member_invite.delete");
  const canUpdateMember = userDetails?.permissions?.includes("member.update");
  const canDeleteMember = userDetails?.permissions?.includes("member.delete");
  const canViewJoinRequest = userDetails?.permissions?.includes("join_request.read");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const activeTeam = team.filter((e) => e.status !== "soft_deleted");
  const deletedTeam = team.filter((e) => e.status === "soft_deleted");

  const [modalData, setModalData] = useState({});
  const toast = useToast();
  const showToast = ({ title = "", status = "success" }) => {
    toast({
      title,
      status,
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
  };

  const { isOpen: invitationIsOpen, onOpen: invitationOnOpen, onClose: invitationOnClose } = useDisclosure();
  const { isOpen: deleteIsOpen, onOpen: deleteOnOpen, onClose: deleteOnClose } = useDisclosure();
  const { isOpen: deleteInviteIsOpen, onOpen: deleteInviteOnOpen, onClose: deleteInviteOnClose } = useDisclosure();
  const { isOpen: editIsOpen, onOpen: editOnOpen, onClose: editOnClose } = useDisclosure();
  const { isOpen: acceptIsOpen, onOpen: acceptOnOpen, onClose: acceptOnClose } = useDisclosure();
  const { isOpen: deleteReqIsOpen, onOpen: deleteReqOnOpen, onClose: deleteReqOnClose } = useDisclosure();

  useEffect(() => {
    try {
      const fetchRoles = async () => {
        setLoading(true);
        const response = await callApiWithToken.get("/customer/roles");
        if (response.status !== 200) {
          throw new Error("Unable to fetch roles");
        }
        const roles = response?.data?.data?.roles;
        setRoles(roles);
      };
      fetchRoles();
    } catch (err) {
      showToast("Error", err.message || "Unable to fetch roles", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const formattedDate = (date) => {
    const originalDate = new Date(date);
    const options = { day: "2-digit", month: "short", year: "numeric" };
    const formattedDate = originalDate.toLocaleDateString("en-US", options);
    return formattedDate;
  };
  const handleInviteTeammate = () => {
    invitationOnOpen();
  };

  const handleDelete = (e) => {
    setModalData(e);
    deleteOnOpen();
  };

  const handleDeleteInvite = (e) => {
    setModalData(e);
    deleteInviteOnOpen();
  };

  const handleEditTeammate = (e) => {
    setModalData(e);
    editOnOpen();
  };

  const handleAcceptRequest = (e) => {
    setModalData(e);
    acceptOnOpen();
  };

  const handleDeleteRequest = (e) => {
    setModalData(e);
    deleteReqOnOpen();
  };

  return (
    <Box py={10}>
      {loading && <Loader />}
      <Flex alignItems={"center"} justify={"space-between"}>
        <Box>
          <Typography color="primary" colorweight="500" type="body" weight="medium">
            Manage Team
          </Typography>
          <Typography color="secondary" colorweight="800" type="caption" weight="regular">
            Invite team members, assign roles & give them permissions
          </Typography>
        </Box>
        {canCreateInvitation && (
          <Button
            bg={"#1A4FD6"}
            fontWeight={"400"}
            color={"#FFFFFF"}
            h={"32px"}
            w={"150px"}
            fontSize={"12px"}
            fontFamily={"DM Sans"}
            onClick={handleInviteTeammate}
            _hover={{ bg: "#366cf5" }}
          >
            Invite Teammate +
          </Button>
        )}
      </Flex>

      {joinRequests.length > 0 && canViewJoinRequest && (
        <Box mt={8}>
          <Typography color="secondary" colorweight="800" type="description" weight="medium">
            <InfoOutlineIcon color={"#E90000"} mr={1} mb={1} /> Join requests {`(${joinRequests.length})`}
          </Typography>

          {joinRequests.map((e, i) => (
            <Box key={i} mt={5} px={"16px"} py={"22px"} bg={"#F2F2F2"} border={"1px"} borderColor={"#E4E4E4"} rounded={"6px"}>
              <Flex justify={"space-between"} alignItems={"center"}>
                <Typography color="secondary" colorweight="800" type="description" weight="medium">
                  {e.email}
                </Typography>
                <Flex gap={5}>
                  {canDeleteInvitation && (
                    <Button
                      h={"32px"}
                      size={"sm"}
                      bg={"#FFFFFF"}
                      border={"1px"}
                      borderColor={"#F2F2F2"}
                      rounded={"6px"}
                      _hover={{ cursor: "pointer" }}
                      onClick={() => handleDeleteRequest(e)}
                    >
                      <Icon as={CloseIcon} color={"red.400"} h={"12px"} />
                    </Button>
                  )}
                  {canUpdateMember && (
                    <Button
                      h={"32px"}
                      size={"sm"}
                      bg={"#1A4FD6"}
                      border={"1px"}
                      borderColor={"#F2F2F2"}
                      rounded={"6px"}
                      _hover={{ cursor: "pointer" }}
                      onClick={() => handleAcceptRequest(e)}
                    >
                      <Icon as={CheckIcon} color={"#FFFFFF"} />
                    </Button>
                  )}
                </Flex>
              </Flex>
            </Box>
          ))}
        </Box>
      )}

      <TableContainer mt={10} height={"70vh"} overflowY={"scroll"} position={"relative"}>
        <Table variant="simple">
          <Tbody>
            <Tr position={"sticky"} zIndex={2} top={0} bg={"#FFFFFF"}>
              <Td>
                <Typography color="secondary" colorweight="600" type="body" weight="bold">
                  User
                </Typography>
              </Td>
              <Td>
                <Typography color="secondary" colorweight="600" type="body" weight="bold">
                  Role
                </Typography>
              </Td>
              <Th></Th>
            </Tr>
            <TableContent
              team={activeTeam}
              formattedDate={formattedDate}
              handleEditTeammate={handleEditTeammate}
              handleDeleteInvite={handleDeleteInvite}
              handleDelete={handleDelete}
              canUpdateMember={canUpdateMember}
              canDeleteMember={canDeleteMember}
            />
            <TableContent
              team={deletedTeam}
              formattedDate={formattedDate}
              handleEditTeammate={handleEditTeammate}
              handleDeleteInvite={handleDeleteInvite}
              handleDelete={handleDelete}
              canUpdateMember={canUpdateMember}
              canDeleteMember={canDeleteMember}
            />
          </Tbody>
        </Table>
      </TableContainer>
      <InvitationModal onClose={invitationOnClose} isOpen={invitationIsOpen} refreshData={refreshData} roles={roles} showToast={showToast} team={team} />
      <DeleteModal onClose={deleteOnClose} isOpen={deleteIsOpen} data={modalData} refreshData={refreshData} showToast={showToast} />
      <DeleteInviteModal onClose={deleteInviteOnClose} isOpen={deleteInviteIsOpen} data={modalData} refreshData={refreshData} showToast={showToast} />
      <EditModal onClose={editOnClose} isOpen={editIsOpen} data={modalData} refreshData={refreshData} roles={roles} showToast={showToast} />
      <AcceptReqModal onClose={acceptOnClose} isOpen={acceptIsOpen} data={modalData} refreshData={refreshData} roles={roles} showToast={showToast} />
      <DeleteReqModal onClose={deleteReqOnClose} isOpen={deleteReqIsOpen} data={modalData} refreshData={refreshData} showToast={showToast} />
    </Box>
  );
};

export default Teams;

const TableContent = ({ team, formattedDate, handleEditTeammate, handleDeleteInvite, handleDelete, canUpdateMember, canDeleteMember }) => {
  return (
    <>
      {team?.map((e, index) => (
        <Tr key={index}>
          <Td>
            <Typography color="secondary" colorweight={e.status !== "soft_deleted" ? "800" : "400"} type="description" weight="medium">
              {e.email}
            </Typography>
          </Td>
          <Td>
            {e.appRole?.map((el, index) => (
              <Typography key={index} color="secondary" colorweight={e.status !== "soft_deleted" ? "800" : "400"} type="description" weight="medium">
                {el}
              </Typography>
            ))}
          </Td>
          <Td>
            <Flex justify={"flex-end"} gap={5}>
              {e.status !== "soft_deleted" && e.inviteOptions && e.inviteOptions?.isInvitationValid && !e.inviteOptions?.isInvitationAccepted && (
                <Flex direction={"column"} justify={"center"} align={"center"}>
                  <Typography key={index} color="secondary" colorweight="800" type="caption" weight="medium">
                    Invite Sent
                  </Typography>
                  <Typography key={index} color="secondary" colorweight="800" type="caption" weight="medium">
                    {formattedDate(e.inviteOptions.invitationSentOn)}
                  </Typography>
                </Flex>
              )}

              {e.status !== "soft_deleted" && e.inviteOptions && e.inviteOptions?.isInvitationValid && e.inviteOptions?.invitationAcceptedOn && (
                <Flex direction={"column"} justify={"center"} align={"center"}>
                  <Typography key={index} color="secondary" colorweight="800" type="caption" weight="medium">
                    Invite Accepted
                  </Typography>
                  <Typography key={index} color="secondary" colorweight="800" type="caption" weight="medium">
                    {formattedDate(e.inviteOptions.invitationAcceptedOn)}
                  </Typography>
                </Flex>
              )}

              {e.status === "soft_deleted" && (
                <Flex direction={"column"} justify={"center"} align={"center"}>
                  <Typography key={index} color="secondary" colorweight="400" type="caption" weight="medium">
                    User Deleted
                  </Typography>
                  <Typography key={index} color="secondary" colorweight="400" type="caption" weight="medium">
                    {formattedDate(e.updatedAt)}
                  </Typography>
                </Flex>
              )}

              <Flex justify={"center"} align={"center"} gap={5}>
                <Button
                  h={"32px"}
                  size={"sm"}
                  bg={"#FFFFFF"}
                  border={"1px"}
                  borderColor={"#F2F2F2"}
                  rounded={"6px"}
                  _hover={{ cursor: "pointer" }}
                  onClick={() => handleEditTeammate(e)}
                  isDisabled={e.appRole.includes("admin") || !canUpdateMember || e.status === "soft_deleted"}
                >
                  <Icon as={MdOutlineModeEdit} color={"#1A4FD6"} />
                </Button>
                {e.inviteOptions && e.inviteOptions?.isInvitationValid && !e.inviteOptions?.isInvitationAccepted ? (
                  <Button
                    h={"32px"}
                    size={"sm"}
                    bg={"#FFFFFF"}
                    border={"1px"}
                    borderColor={"#F2F2F2"}
                    rounded={"6px"}
                    _hover={{ cursor: "pointer" }}
                    onClick={() => handleDeleteInvite(e)}
                    isDisabled={e.appRole.includes("admin") || !canDeleteMember || e.status === "soft_deleted"}
                  >
                    <Icon as={BsTrash} color={"red.400"} />
                  </Button>
                ) : (
                  <Button
                    h={"32px"}
                    size={"sm"}
                    bg={"#FFFFFF"}
                    border={"1px"}
                    borderColor={"#F2F2F2"}
                    rounded={"6px"}
                    _hover={{ cursor: "pointer" }}
                    onClick={() => handleDelete(e)}
                    isDisabled={e.appRole.includes("admin") || !canDeleteMember || e.status === "soft_deleted"}
                  >
                    <Icon as={BsTrash} color={"red.400"} />
                  </Button>
                )}
              </Flex>
            </Flex>
          </Td>
        </Tr>
      ))}
    </>
  );
};
